/**
 * Captions for a game that is almost entirely sound.
 *
 * Every other accessibility feature here is a courtesy; this one decides whether the game can be
 * played at all without hearing it. NightBand's whole verb is *listening*, and a player who
 * cannot hear has to be given the same information by other means — not a transcript of the
 * dialogue (the lines are already on screen), but a description of everything that is *not*
 * speech, which is most of what is happening.
 *
 * ## What a caption has to say that the interface does not
 *
 * The receiving line already names the station: "VIG-3 · identification". That tells a hearing
 * player nothing they did not know and tells a deaf player almost nothing at all — it does not
 * say that the sound is slow morse, repeating, and that it has been going for hours. The label is
 * *who*; the caption is *what it sounds like*. Both are needed, which is why this is a separate
 * line rather than a longer version of the existing one.
 *
 * ## Two kinds of caption
 *
 * A **state** caption describes the sound that is currently there and stays until it changes: the
 * static, the signal you are tuned to. A **transient** caption describes something that happened
 * and is already over — a ghost fragment, a burst of interference — and clears itself.
 *
 * They are kept apart because a transient must never destroy the state: hearing a ghost while
 * tuned to a station should not leave a deaf player believing the station stopped.
 */
import { Store } from '../state/store.js';
import type { StringKey } from '../i18n/index.js';

export interface Caption {
  /** What is being heard now. Empty when the set is off. */
  state: string;
  /** Something that just happened, or null. Clears itself. */
  transient: string | null;
}

/** How long a one-off caption stays up. Long enough to read a short phrase without hurrying. */
const TRANSIENT_MS = 3500;

export class CaptionChannel {
  readonly caption = new Store<Caption>({ state: '', transient: null });

  private timer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Describe the continuous sound.
   *
   * Idempotent: setting the same description twice does not disturb the store, because this is
   * driven by a poll and an `aria-live` region that re-announced identical text four times a
   * second would be unusable.
   */
  setState(text: string): void {
    if (this.caption.get().state === text) return;
    this.caption.set({ ...this.caption.get(), state: text });
  }

  /** Describe something that happened. Replaces any pending transient and restarts the clock. */
  push(text: string): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.caption.set({ ...this.caption.get(), transient: text });
    this.timer = setTimeout(() => {
      this.timer = null;
      this.caption.set({ ...this.caption.get(), transient: null });
    }, TRANSIENT_MS);
  }

  dispose(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }
}

/**
 * The locale key describing what a synth preset sounds like.
 *
 * Keyed off the preset rather than authored per signal, so every parasite signal an author adds
 * is captioned the moment it exists and nobody has to remember to write one. An author who wants
 * something more specific can still say so — see `describeSignal`.
 *
 * The cast is the one place the typed `t()` seam has to be widened: the key is built from a
 * preset name that is data. A test asserts every shipped preset has a string, which is the same
 * guarantee the type would have given, checked where the data actually lives.
 */
export function presetCaptionKey(preset: string): StringKey {
  return `caption.preset.${preset}` as StringKey;
}

export interface CaptionableSignal {
  label?: string;
  source: { type: 'synth'; preset: string } | { type: 'audio'; url: string };
  /** An author's own description, when the preset's generic one will not do. */
  caption?: string;
}

/**
 * Build the caption for whatever the receiver is holding.
 *
 * `translate` is passed in rather than imported so this stays pure and testable, and so a missing
 * key can be handled by the caller's own fallback rules.
 */
export function describeSignal(
  signal: CaptionableSignal | null,
  translate: (key: StringKey, vars?: Record<string, string | number>) => string,
): string {
  if (!signal) return translate('caption.static');

  // An authored description always wins: the preset only knows what it sounds like in general,
  // and an author may need to say what *this* one sounds like.
  const sound = signal.caption
    ? signal.caption
    : signal.source.type === 'audio'
      ? translate('caption.preset.voice')
      : translate(presetCaptionKey(signal.source.preset));

  return signal.label
    ? translate('caption.signal', { label: signal.label, sound })
    : translate('caption.signalUnknown', { sound });
}
